/* global axios */
import ApiClient from './ApiClient';
import { shallowReactive } from 'vue';
import store from 'dashboard/store';

const entries = new Map();
const countryContexts = shallowReactive(new Map());
const pending = new Map();
let scope;
const MAX_ENTRIES = 30;
const TTL = 60000;
const SNAPSHOT_TTL = 10 * 60000;

class ConversationLingxingOrdersAPI extends ApiClient {
  constructor() {
    super('conversations', { accountScoped: true });
  }

  cacheKey(conversationId) {
    const current = `${this.url}:${store.getters.getCurrentUser?.id || 'anonymous'}`;
    if (scope !== current) {
      entries.clear();
      countryContexts.clear();
      pending.clear();
      scope = current;
    }
    return this.url + '/' + conversationId + '/lingxing_orders';
  }

  getCountryContext(conversationId) {
    return countryContexts.get(this.cacheKey(conversationId)) || null;
  }

  getCached(conversationId, { allowStale = false } = {}) {
    const key = this.cacheKey(conversationId);
    const entry = entries.get(key);
    if (!entry || Date.now() - entry.at >= SNAPSHOT_TTL) {
      entries.delete(key);
      countryContexts.delete(key);
      return null;
    }
    if (!allowStale && Date.now() - entry.at >= TTL) return null;
    entries.delete(key);
    entries.set(key, entry);
    return entry.data;
  }

  remember(conversationId, data) {
    this.storeCache(this.cacheKey(conversationId), data);
  }

  storeCache(key, data) {
    entries.delete(key);
    entries.set(key, { data, at: Date.now() });
    countryContexts.set(key, data.country_context || null);
    while (entries.size > MAX_ENTRIES) {
      const oldest = entries.keys().next().value;
      entries.delete(oldest);
      countryContexts.delete(oldest);
    }
  }

  invalidateKey(key) {
    entries.delete(key);
    countryContexts.delete(key);
    pending.delete(key);
  }

  async lookup(conversationId, orderNumber) {
    const key = this.cacheKey(conversationId);
    this.invalidateKey(key);
    try {
      return await axios.post(key, { order_number: orderNumber }, { timeout: 15000 });
    } finally {
      this.invalidateKey(key);
    }
  }

  async unlink(conversationId, linkId) {
    const key = this.cacheKey(conversationId);
    this.invalidateKey(key);
    try {
      return await axios.delete(key, { data: { link_id: linkId } });
    } finally {
      this.invalidateKey(key);
    }
  }

  prefetch(conversationId) {
    if (this.getCached(conversationId)) return Promise.resolve();
    return this.getForConversation(conversationId).catch(() => {});
  }

  getForConversation(conversationId, { force = false } = {}) {
    const key = this.cacheKey(conversationId);
    if (!force && pending.has(key)) return pending.get(key);
    const requestScope = scope;
    const request = axios.get(key, {
      timeout: 15000,
      ...(force ? { params: { _refresh: Date.now() } } : {}),
    }).then(response => {
      if (scope === requestScope && pending.get(key) === request) {
        this.storeCache(key, response.data);
      }
      return response;
    }).catch(error => {
      if (pending.get(key) === request && [401, 403, 404].includes(error?.response?.status)) {
        entries.delete(key);
        countryContexts.delete(key);
      }
      throw error;
    }).finally(() => {
      if (pending.get(key) === request) pending.delete(key);
    });
    pending.set(key, request);
    return request;
  }
}

export default new ConversationLingxingOrdersAPI();
